import { useEffect, useState } from 'preact/hooks'

import { formatMoney, globalState } from 'helpers/utils'

import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import useCartGlobal, { CartConfigKeys } from 'hooks/useCartGlobal'
import useURL from 'hooks/useUrl'

import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'
import { createPortal } from 'preact/compat'
import clsx from 'clsx'
import { usePromolines } from '@/components/hooks/usePromolines'

const actionKeys = {
  PLUS: 'plus',
  MINUS: 'minus',
  REMOVE: 'remove'
}

const onChangeActionValue = (key, quantity) => {
  return {
    [actionKeys.PLUS]: quantity + 1,
    [actionKeys.MINUS]: quantity - 1,
    [actionKeys.REMOVE]: 0
  }[key]
}

const OnChangeTrigger = ({ type, value, className, p }) => {
  return (
    <div class={`group shrink-0 relative cursor-pointer ${p}`}>
      <input
        type="checkbox"
        data-action={type}
        class=" absolute inset-tl size-full cursor-pointer appearance-none border-0 bg-transparent opacity-0 z-2"
        aria-label={value}
      />
      <div class={className}>{value}</div>
    </div>
  )
}

/**
 *
 * Handles line item quantity update.
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemQuantity = ({ item, handleSetCartConfig }) => {
  const plusIcon = <Icon id="plus" />
  const minusIcon = <Icon id="minus" />
  const trashIcon = <>Remove</>
  const [error, setError] = useState(null)

  /** Checks if the current Line Item new quantity is above the available inventory  */
  const handleLineItemQuantityChange = (item, newQuantity) => {
    if (newQuantity > item.inventory_quantity) {
      setError('Maximum quantity reached')
      setTimeout(() => setError(null), 5000)
      throw new Error('Unavailable product stock quantity')
    }
    setError(null)
  }

  const onLineItemQuantityChange = async (e) => {
    try {
      e.preventDefault()
      /** Get Action Key */
      const action = e.target.dataset.action
      /** Set MiniCart state to isLoading true */
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      /** Get new line item quantity based off action */
      const newQuantity = onChangeActionValue(action, item.quantity)
      /** Throw an error is the new quantity is over the available stock */
      handleLineItemQuantityChange(item, newQuantity)
      /** Update line item quantity */
      await window.Cart.updateLineItemQuantity(
        {
          id: item.variant_id,
          quantity: newQuantity
        },
        { silent: false }
      )

      /** Poll Shopify for new Cart item and set cart config state */
      await window.Cart.getCart()
      handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    } catch (error) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: error.message
      })
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    }
  }

  return (
    <>
    <form onChange={onLineItemQuantityChange} class="flex items-center justify-between gap-x-0.5 md:gap-x-1">
      <div class="shrink-0 flex border border-neutral-medium">
        <OnChangeTrigger
          p="btn btn-primary btn-transparent btn-square w-[36px] !border-0"
          type={actionKeys.MINUS}
          value={minusIcon}
          className={'size-fit children:w-[14px] children:h-[2px]'}
        />
        <div class="grow flex items-center justify-center text-s w-2 text-center">{item.quantity}</div>
        <OnChangeTrigger
          p='btn btn-primary btn-transparent btn-square w-[36px] !border-0'
          type={actionKeys.PLUS}
          value={plusIcon}
          className={'size-fit children:size-[14px]'}
        />
      </div>

      <OnChangeTrigger
        p="btn btn-primary btn-text btn-s text-xs"
        type={actionKeys.REMOVE}
        value={trashIcon}
        className={''}
      />
    </form>
    <p class={clsx('hidden w-full paragraph-xs text-system-error transition-all', error && '!flex')}>{error}</p>
    </>
  )
}

/**
 *
 *
 * Mini Cart Snippet,
 * Currently hardcoded to handle the free shipping discount range.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartSnippet({ cartSubTotal, freeShippingGoal }) {
  const handleFreeShippingValue = (amount) => {
    if (typeof amount === 'number' && amount > 0) {
      return amount
    }
    return parseInt(amount)
  }

  const freeShippingMinimum = handleFreeShippingValue(freeShippingGoal) ?? 10000
  const reachedGoal = freeShippingMinimum - cartSubTotal
  const message = reachedGoal <= 0 ? (
    <>
      <div class="flex w-[11px] h-[8px] children:size-full">
        <Icon id={'tick'} />
      </div>
      <p class="paragraph-xs whitespace-nowrap overflow-hidden text-ellipsis">You're receiving free standard shipping</p>
    </>
  ) : (
    <>
      <p class="paragraph-xs whitespace-nowrap overflow-hidden text-ellipsis">You're <span class="font-medium">{formatMoney(reachedGoal)}</span> away from free standard shipping</p>
    </>
  )
  const progressBarPercentage = Math.round((cartSubTotal / freeShippingGoal) * 100)
  const progressBarPercentageWidth = progressBarPercentage > 100 ? '100' : progressBarPercentage
  return (
    <div data-role="free_shipping_monitor" class={clsx(
      'flex flex-col gap-0.5 px-1 md:px-1.5 py-1 transition-all',
      reachedGoal <= 0 ? 'bg-system-success-light' : 'bg-neutral-light'
    )}>
      <div class="flex items-center justify-center gap-x-0.375 text-center">
        {message}
      </div>
      <div class="relative w-full h-[4px] bg-neutral-tertiary">
        <div
          class="absolute inset-tl w-shipping-bar-width h-full bg-brand-black transition-all"
          style={{ '--shipping-bar-width': progressBarPercentageWidth + '%' }}
        ></div>
      </div>
    </div>
  )
}

/**
 *
 * Display line item price
 * Handles different price if discount is applied
 *
 * Props Cart Line Item
 *
 * @param {*} param0
 * @returns
 */
const ItemPrice = ({ item }) => {
  const DiscountedPrice = ({ newPrice, oldPrice }) => {
    return (
      <div class="flex flex-col items-end text-right">
        <p class="paragraph-s line-through">{formatMoney(oldPrice)}</p>
        <p class="paragraph-s text-brand-sale">{formatMoney(newPrice)}</p>
      </div>
    )
  }

  if (item.discounts.length > 0 && item.original_line_price !== item.line_price) {
    return <DiscountedPrice newPrice={item.line_price} oldPrice={item.original_line_price} />
  } else if (item.compare_at_price > item.original_price) {
    return <DiscountedPrice newPrice={item.line_price} oldPrice={item.compare_at_price * item.quantity} />
  }
  return (
    <div class="flex flex-col items-end text-right">
      <p class="paragraph-s">{formatMoney(item.original_line_price)}</p>
    </div>
  )
}

const MiniCartItemDiscount = ({ item }) => {
  return (
    <>
      {item.discounts.map((discount) => {
        return <div class="paragraph-xs uppercase"><span>{discount.title}</span></div>
      })}
    </>
  )
}

/**
 *
 * Displays line item image
 *
 * @param {*} imageSrc
 * @param {*} id
 * @param {*} productPath
 * @returns
 */
const MiniCartItemImage = ({ imageSrc, id, productPath }) => {
  return (
    <a href={productPath} class="shrink-0 link-fade" aria-label="Item image">
      <PreactImage className="flex w-[88px] md:w-[120px] aspect-4/5 children:size-full children:object-cover" key={id} imageUrl={[imageSrc]} />
    </a>
  )
}

/**
 *
 * Display the line item
 * 1) Title
 * 2) Variant Details (size, colour)
 * 3) Discount details (if applicable)
 * 4) Line Item price
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemDetails = ({ item }) => {
  const properties = item.properties
  const hasProperties = properties && Object.keys(properties).length > 0

  const criteria = {
    tags: item.tags ?? [],
    collections: item.collections?.map(c => c.handle) ?? [],
    products: [item.handle]
  }
  const [firstPromoline, secondPromoline] = usePromolines(criteria)

  return (
    <div class="flex flex-row justify-between gap-x-0.5 md:gap-x-1">
      <div class="flex flex-col gap-y-0.5">
        <h3 class="paragraph-s">
          {item.product_title}
        </h3>
        <p class="paragraph-xs text-neutral-secondary">
          {item.options_with_values.map((option, index) => (
            <>
              {option.value}
              {index < item.options_with_values.length - 1 && <span class="text-neutral-medium"> | </span>}
            </>
          ))}
        </p>
        {hasProperties && (
          <p class="paragraph-xs text-neutral-secondary">
            {properties && Object.keys(properties)
              .filter(property => properties[property])
              .map((property, index) => (
                <>
                  {property}: {properties[property]}
                  {index < Object.keys(properties).length - 1 && <br />}
                </>
              ))}
          </p>
        )}
        {(firstPromoline || secondPromoline) && (
          <div class="flex flex-col gap-y-0.25">
            {firstPromoline && (
              <div
              class="flex w-fit text-[--text-colour]"
              style={{ '--text-colour': firstPromoline.text_colour }}
              >
                <span class="paragraph-xs">{firstPromoline.title}</span>
              </div>
            )}
            {secondPromoline && (
              <div
                class="flex w-fit text-[--text-colour]"
                style={{ '--text-colour': secondPromoline.text_colour }}
              >
                <span class="paragraph-xs">{secondPromoline.title}</span>
              </div>
            )}
          </div>
        )}
      </div>
      <ItemPrice item={item} />
    </div>
  )
}

const MiniCartLineItem = ({ item, handleSetCartConfig, isLoading }) => {
  const loadingState = isLoading && 'opacity-50'

  return (
    <>
      <div class={`flex flex-row gap-x-0.5 md:gap-x-1 py-1.5 border-b border-neutral-medium transition-all ${loadingState}`}>
        <MiniCartItemImage imageSrc={item.featured_image.url} id={item.id} productPath={item.url} />
        <div class="flex-grow flex flex-col gap-y-1 w-full">
          <MiniCartItemDetails item={item} />
          <MiniCartItemQuantity item={item} handleSetCartConfig={handleSetCartConfig} />
          <MiniCartItemDiscount item={item} />
        </div>
      </div>
    </>
  )
}

/**
 *
 * Displays cart total for Mini Cart.
 *
 * @param {*} param0
 * @returns
 */
const DisplayCartTotal = ({ total, totalCartDiscount, isLoading, cart, discountText, totalText }) => {
  const cartTotal = formatMoney(total)
  const totalDiscount = formatMoney(totalCartDiscount)
  const subtotal = formatMoney(total + totalCartDiscount)
  const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0)

  return (
    <div class="flex flex-col">
      <x-modal
        data-id="total-order-summary"
        class="group flex flex-col w-full data-visible:pb-1 transition-all"
      >
        <button
          class="flex items-center justify-between gap-x-0.75 pt-1.25 pb-0.625 link-fade"
          data-modal-parent="total-order-summary"
          data-modal-role="toggle"
          aria-label="Show order summary"
        >
          <span class="paragraph-s font-medium before:content-['Show '] group-data-visible:before:content-['Hide ']">Order Summary</span>
          <div class="w-[14px] h-[8px] group-data-visible:-rotate-180 transition-all children:size-full">
              <Icon id={'chevron-down'} />
          </div>
        </button>
        <div class="flex flex-col max-h-0 opacity-0 group-data-visible:pt-0.625 group-data-visible:opacity-100 group-data-visible:max-h-[150vh] group-data-visible:opacity-100 overflow-hidden transition-all">
          <div class={`flex flex-col gap-y-0.5 transition-all ${isLoading && 'opacity-50'}`}>
            <div class="flex justify-between gap-x-1">
              <p class="paragraph-s">{totalItems} {totalItems === 1 ? 'Item' : 'Items'}</p>
              <p class={`paragraph-s transition-all ${isLoading && 'opacity-50'}`}>{subtotal}</p>
            </div>
            <div class="flex justify-between gap-x-1">
              <p class="paragraph-s">{discountText || 'Discounts'}</p>
              <p class={`paragraph-s transition-all ${isLoading && 'opacity-50'}`}>-{totalDiscount}</p>
            </div>
          </div>
        </div>
      </x-modal>
      <div class="flex justify-between gap-x-1">
        <p class="paragraph-m font-medium">{totalText || 'Total'}</p>
        <p class={`paragraph-m font-medium transition-all ${isLoading && 'opacity-50'}`}>{cartTotal}</p>
      </div>
    </div>
  )
}

/**
 *
 * Empty Cart state. Renders when the customers cart is empty
 *
 * @param {*} param0
 * @returns
 */
const EmptyCart = ({ isLoading, emptyDescription, emptyLink, emptyLinkText }) => {
  return (
    <>
      <div class={`grow flex flex-col gap-y-1 md:gap-y-1.5 items-center justify-center px-margin py-6.25 text-center transition-all ${isLoading ? 'opacity-0' : ''}`}>
        <p class={'h6 font-medium'}>{emptyDescription}</p>
        {emptyLinkText &&
          <a href={emptyLink} className={'btn btn-primary btn-fill btn-m btn-bold w-fit'} aria-label={emptyLinkText}>{emptyLinkText}</a>
        }
      </div>
    </>
  )
}

/**
 *
 * Cart Checkout button
 *
 * @param {*} param0
 * @returns
 */
const CheckoutButton = ({ checkoutButtonTitle }) => {
  return (
    <div>
      <a href="/checkout" class="btn btn-accent btn-fill btn-l btn-heading w-full uppercase" aria-label={checkoutButtonTitle}>
        {checkoutButtonTitle}
      </a>
    </div>
  )
}

/**
 *
 * Displays the line items present in the the customers cart.
 *
 * @param {*} param0
 * @returns
 */
const CartLineItems = ({ cart, handleSetCartConfig, isLoading }) => {
  return (
    <div class="grow flex flex-col overflow-y-scroll no-scrollbar overscroll-none px-1 md:px-1.5 pb-2" data-role="cart_items">
      {cart.items.map((item) => {
        return <MiniCartLineItem item={{ ...item }} handleSetCartConfig={handleSetCartConfig} isLoading={isLoading} />
      })}
    </div>
  )
}

/**
 *
 * Mini Cart
 *
 * @returns
 */
export function MiniCart() {
  const path = useURL()
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const globalCart = globalState.cart
  const { isLoading, cart } = cartConfig
  const freeShippingMinimum = globalCart?.freeShippingMinimum

  const { checkoutButtonTitle, emptyDescription, emptyLink, emptyLinkText, discountText, totalText } = globalCart

  useEffect(() => {
    const altPaymentButtons = document.querySelector('div.additional-checkout-buttons--vertical')
    if (!altPaymentButtons) return
    if (cart.items.length > 0) {
      altPaymentButtons.classList.remove('hidden')
    } else if (cart.items.length <= 0) {
      altPaymentButtons.classList.add('hidden')
    }
  }, [cartConfig])

  useEffect(() => {
    if (path.includes('timestamp=')) {
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      window.Cart.getCart().then(() => {
        handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
        handleSetCartConfig(CartConfigKeys.isLoading, false)
      })
    }
  }, [path])

  return (
    <>
      {cart?.items?.length ? (
        <>
          <MiniCartPortal targetId="minicart-shipping">
            <MiniCartSnippet cartSubTotal={cart.items_subtotal_price} freeShippingGoal={freeShippingMinimum} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-items">
            <CartLineItems cart={cart} isLoading={isLoading} handleSetCartConfig={handleSetCartConfig} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-summary">
            <div className="flex flex-col gap-y-1">
              <DisplayCartTotal
                totalCartDiscount={cart.total_discount}
                total={cart.total_price}
                isLoading={isLoading}
                cart={cart}
                discountText={discountText}
                totalText={totalText}
              />
              <CheckoutButton checkoutButtonTitle={checkoutButtonTitle} />
            </div>
          </MiniCartPortal>
        </>
      ) : (
        <MiniCartPortal targetId="minicart-items">
          <EmptyCart cartConfig={cartConfig} globalCart={globalCart} isLoading={isLoading} emptyDescription={emptyDescription} emptyLink={emptyLink} emptyLinkText={emptyLinkText} />
        </MiniCartPortal>
      )}
    </>
  )
}

const MiniCartPortal = ({ children, targetId }) => {
  const target = document.getElementById(targetId)
  return target ? createPortal(children, target) : null
}
